import React from 'react';
import { Plus, Save, X, Loader2, FileText, Upload, Trash2, Edit2, Building2 } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';

const CaseStudyForm = ({
  formData,
  setFormData,
  editingStudy,
  activeTab,
  setActiveTab,
  onSubmit,
  onClose,
  isSaving,
  isUploading,
  imagePreview,
  setImagePreview,
  fileInputRef,
  onImageUpload,
}) => {
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleTestimonialChange = (field, value) => {
    setFormData(prev => ({ ...prev, testimonial: { ...prev.testimonial, [field]: value } }));
  };

  const handleResultChange = (index, field, value) => {
    const newResults = [...formData.results];
    newResults[index] = { ...newResults[index], [field]: value };
    setFormData(prev => ({ ...prev, results: newResults }));
  };

  const addResult = () => {
    setFormData(prev => ({ ...prev, results: [...prev.results, { metric: '', label_en: '', label_tr: '' }] }));
  };

  const removeResult = (index) => {
    setFormData(prev => ({ ...prev, results: prev.results.filter((_, i) => i !== index) }));
  };

  const handleProgramChange = (lang, index, value) => {
    const field = `programs_${lang}`;
    const newPrograms = [...formData[field]];
    newPrograms[index] = value;
    setFormData(prev => ({ ...prev, [field]: newPrograms }));
  };

  const addProgram = (lang) => {
    const field = `programs_${lang}`;
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeProgram = (lang, index) => {
    const field = `programs_${lang}`;
    setFormData(prev => ({ ...prev, [field]: prev[field].filter((_, i) => i !== index) }));
  };

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-8" data-testid="case-study-form-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl mx-4 my-8">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-bold text-gray-900">
            {editingStudy ? 'Edit Case Study' : 'Create New Case Study'}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        <form onSubmit={onSubmit} className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
          {/* Language Tabs */}
          <div className="flex gap-2 border-b border-gray-200 pb-4">
            <button type="button" onClick={() => setActiveTab('en')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'en' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              EN English
            </button>
            <button type="button" onClick={() => setActiveTab('tr')}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${activeTab === 'tr' ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
              TR Turkish
            </button>
            <span className="ml-auto text-sm text-gray-500 self-center">
              {activeTab === 'en' ? 'Required fields' : 'Optional - falls back to English'}
            </span>
          </div>

          {/* Company Name */}
          <div>
            <Label htmlFor="company">Company Name *</Label>
            <Input id="company" name="company" value={formData.company} onChange={handleInputChange}
              required placeholder="e.g., TechCorp Global" className="mt-2" />
          </div>

          {/* Industry */}
          <div>
            <Label htmlFor={`industry_${activeTab}`}>Industry {activeTab === 'en' && '*'}</Label>
            <Input id={`industry_${activeTab}`} name={`industry_${activeTab}`}
              value={formData[`industry_${activeTab}`]} onChange={handleInputChange}
              required={activeTab === 'en'} placeholder={activeTab === 'en' ? 'e.g., Technology' : 'e.g., Teknoloji'}
              className="mt-2" />
          </div>

          {/* Hero Image */}
          <div>
            <Label>Hero Image *</Label>
            <div className="mt-2 space-y-3">
              <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-blue-400 transition-colors cursor-pointer"
                onClick={() => fileInputRef.current?.click()}>
                <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/gif,image/webp"
                  onChange={onImageUpload} className="hidden" data-testid="hero-image-upload" />
                {isUploading ? (
                  <div className="flex flex-col items-center gap-2">
                    <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
                    <span className="text-sm text-gray-500">Uploading...</span>
                  </div>
                ) : (
                  <div className="flex flex-col items-center gap-2">
                    <Upload className="w-8 h-8 text-gray-400" />
                    <span className="text-sm text-gray-600">Click to upload image</span>
                    <span className="text-xs text-gray-400">JPG, PNG, GIF, WebP (max 5MB)</span>
                  </div>
                )}
              </div>
              {(imagePreview || formData.heroImage) && (
                <div className="relative inline-block">
                  <img src={imagePreview || formData.heroImage} alt="Preview"
                    className="h-24 w-40 object-cover rounded-lg border border-gray-200"
                    onError={(e) => e.target.style.display = 'none'} />
                  <button type="button" onClick={() => { setFormData(prev => ({ ...prev, heroImage: '' })); setImagePreview(null); }}
                    className="absolute -top-2 -right-2 p-1 bg-red-500 text-white rounded-full hover:bg-red-600">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Challenge */}
          <div>
            <Label htmlFor={`challenge_${activeTab}`}>Challenge {activeTab === 'en' && '*'}</Label>
            <Textarea id={`challenge_${activeTab}`} name={`challenge_${activeTab}`}
              value={formData[`challenge_${activeTab}`]} onChange={handleInputChange}
              required={activeTab === 'en'} rows={4}
              placeholder={activeTab === 'en' ? 'Describe the challenge...' : 'Zorluklari aciklayin...'}
              className="mt-2" />
          </div>

          {/* Solution */}
          <div>
            <Label htmlFor={`solution_${activeTab}`}>Solution {activeTab === 'en' && '*'}</Label>
            <Textarea id={`solution_${activeTab}`} name={`solution_${activeTab}`}
              value={formData[`solution_${activeTab}`]} onChange={handleInputChange}
              required={activeTab === 'en'} rows={4}
              placeholder={activeTab === 'en' ? 'Describe the solution...' : 'Cozumu aciklayin...'}
              className="mt-2" />
          </div>

          {/* Results */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Results {activeTab === 'en' && '*'}</Label>
              <Button type="button" variant="outline" size="sm" onClick={addResult}>
                <Plus className="w-4 h-4 mr-1" /> Add Result
              </Button>
            </div>
            <div className="space-y-3">
              {formData.results.map((result, index) => (
                <div key={`result-${index}`} className="flex gap-2 items-start">
                  <Input placeholder="Metric (e.g., 68%)" value={result.metric}
                    onChange={(e) => handleResultChange(index, 'metric', e.target.value)} className="w-24" />
                  <Input placeholder={activeTab === 'en' ? 'Label (e.g., Reduction in turnover)' : 'Etiket'}
                    value={result[`label_${activeTab}`]}
                    onChange={(e) => handleResultChange(index, `label_${activeTab}`, e.target.value)} className="flex-1" />
                  {formData.results.length > 1 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => removeResult(index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Testimonial */}
          <div className="border border-gray-200 rounded-lg p-4">
            <Label className="text-lg font-semibold mb-4 block">Testimonial</Label>
            <div className="space-y-4">
              <div>
                <Label htmlFor={`quote_${activeTab}`}>Quote {activeTab === 'en' && '*'}</Label>
                <Textarea id={`quote_${activeTab}`} value={formData.testimonial[`quote_${activeTab}`]}
                  onChange={(e) => handleTestimonialChange(`quote_${activeTab}`, e.target.value)}
                  required={activeTab === 'en'} rows={3}
                  placeholder={activeTab === 'en' ? 'Client testimonial quote...' : 'Musteri referans yorumu...'}
                  className="mt-2" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="author">Author Name *</Label>
                  <Input id="author" value={formData.testimonial.author}
                    onChange={(e) => handleTestimonialChange('author', e.target.value)}
                    required placeholder="e.g., Jennifer Walsh" className="mt-2" />
                </div>
                <div>
                  <Label htmlFor={`role_${activeTab}`}>Role {activeTab === 'en' && '*'}</Label>
                  <Input id={`role_${activeTab}`} value={formData.testimonial[`role_${activeTab}`]}
                    onChange={(e) => handleTestimonialChange(`role_${activeTab}`, e.target.value)}
                    required={activeTab === 'en'}
                    placeholder={activeTab === 'en' ? 'e.g., Chief People Officer' : 'e.g., IK Direktoru'}
                    className="mt-2" />
                </div>
              </div>
            </div>
          </div>

          {/* Timeline & Participants */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label htmlFor={`timeline_${activeTab}`}>Timeline {activeTab === 'en' && '*'}</Label>
              <Input id={`timeline_${activeTab}`} name={`timeline_${activeTab}`}
                value={formData[`timeline_${activeTab}`]} onChange={handleInputChange}
                required={activeTab === 'en'} placeholder={activeTab === 'en' ? 'e.g., 12 months' : 'e.g., 12 ay'}
                className="mt-2" />
            </div>
            <div>
              <Label htmlFor={`participants_${activeTab}`}>Participants {activeTab === 'en' && '*'}</Label>
              <Input id={`participants_${activeTab}`} name={`participants_${activeTab}`}
                value={formData[`participants_${activeTab}`]} onChange={handleInputChange}
                required={activeTab === 'en'}
                placeholder={activeTab === 'en' ? 'e.g., 50 senior leaders' : 'e.g., 50 ust duzey lider'}
                className="mt-2" />
            </div>
          </div>

          {/* Programs */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Programs {activeTab === 'en' && '*'}</Label>
              <Button type="button" variant="outline" size="sm" onClick={() => addProgram(activeTab)}>
                <Plus className="w-4 h-4 mr-1" /> Add Program
              </Button>
            </div>
            <div className="space-y-2">
              {formData[`programs_${activeTab}`].map((program, index) => (
                <div key={`prog-${activeTab}-${index}`} className="flex gap-2">
                  <Input placeholder={activeTab === 'en' ? 'e.g., Executive Coaching' : 'e.g., Yonetici Koçlugu'}
                    value={program} onChange={(e) => handleProgramChange(activeTab, index, e.target.value)}
                    className="flex-1" />
                  {formData[`programs_${activeTab}`].length > 1 && (
                    <Button type="button" variant="outline" size="sm" onClick={() => removeProgram(activeTab, index)}>
                      <X className="w-4 h-4" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Featured */}
          <div>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" name="featured" checked={formData.featured} onChange={handleInputChange}
                className="w-5 h-5 rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
              <span className="font-medium text-gray-700">Featured Case Study</span>
            </label>
          </div>

          {/* Submit Buttons */}
          <div className="flex justify-end gap-4 pt-6 border-t border-gray-200">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="button" variant="outline" disabled={isSaving} onClick={(e) => onSubmit(e, 'draft')}
              className="text-gray-600 border-gray-300" data-testid="save-draft-btn">
              {isSaving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileText className="w-4 h-4 mr-2" />}
              Save as Draft
            </Button>
            <Button type="button" disabled={isSaving} onClick={(e) => onSubmit(e, 'published')}
              className="bg-blue-600 hover:bg-blue-700" data-testid="publish-btn">
              {isSaving ? (
                <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Saving...</>
              ) : (
                <><Save className="w-4 h-4 mr-2" />{editingStudy ? 'Update & Publish' : 'Publish'}</>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CaseStudyForm;
